/**
 * PartnerApplyPage — solicitud pública para ser partner (RES-94, épica RES-88).
 *
 * Sin login. Se elige el programa (influencers o community managers / agencias), se completan
 * los datos y queda una solicitud `pending` que el equipo revisa. Incluye un campo trampa
 * (`website`) fuera de pantalla: si viene lleno el backend lo descarta como bot.
 */

import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { createPartnerApplication } from '../services/api';
import { TourProvider } from '../context/TourContext';
import HelpIcon from '../components/help/HelpIcon';
import LanguageSelector from '../components/LanguageSelector';
import {
  APPLY_PROGRAMS,
  buildApplicationPayload,
  getApplicationErrorState,
  initialApplicationForm,
  validateApplicationForm,
} from '../utils/partnerApply';
import './AuthPages.css';
import './PartnerApplyPage.css';

function PartnerApplyContent() {
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [form, setForm] = useState(initialApplicationForm);
  const [fieldError, setFieldError] = useState(null);
  const [error, setError] = useState(null); // 'pending' | 'generic' | null
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldError === name) setFieldError(null);
    setError(null);
  };

  const focusField = (field) => {
    const el = formRef.current?.querySelector(`[name="${field}"]`);
    if (el && typeof el.focus === 'function') el.focus();
  };

  const markField = (field) => {
    setFieldError(field);
    // Esperar al render para enfocar (el campo puede aparecer al elegir programa)
    setTimeout(() => focusField(field), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const invalid = validateApplicationForm(form);
    if (invalid) {
      markField(invalid);
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await createPartnerApplication(buildApplicationPayload(form));
      setSubmitted(true);
    } catch (err) {
      const state = getApplicationErrorState(err);
      if (state.type === 'field') markField(state.field);
      else setError(state.type);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = (name) => `form-group ${fieldError === name ? 'pa-field--error' : ''}`;
  const renderFieldError = (name) =>
    fieldError === name ? (
      <span className="pa-field-error" id={`pa-${name}-error`} role="alert">
        {t(`partnerApply.errors.${name}`)}
      </span>
    ) : null;
  const a11y = (name) =>
    fieldError === name ? { 'aria-invalid': 'true', 'aria-describedby': `pa-${name}-error` } : {};

  const isInfluencer = form.program === 'influencer';
  const isCm = form.program === 'cm';

  return (
    <div className="auth-container pa-container">
      <div className="auth-card pa-card">
        <div className="pa-topbar">
          <LanguageSelector />
        </div>
        <div className="auth-header">
          <h1>Resto</h1>
          <h2>{t('partnerApply.title')}</h2>
        </div>

        {submitted ? (
          <div className="pa-success" role="status" data-testid="pa-success">
            <CheckCircle2 size={48} aria-hidden="true" className="pa-success-icon" />
            <h3>{t('partnerApply.success.title')}</h3>
            <p>{t('partnerApply.success.body')}</p>
            <Link to="/login" className="btn btn-secondary">
              {t('partnerApply.alreadyPartnerCta')}
            </Link>
          </div>
        ) : (
          <form ref={formRef} className="auth-form" onSubmit={handleSubmit} noValidate>
            <p className="auth-subtitle pa-intro">{t('partnerApply.intro')}</p>

            <fieldset
              className={`pa-programs ${fieldError === 'program' ? 'pa-field--error' : ''}`}
              data-field="program"
            >
              <legend>
                {t('partnerApply.programLegend')} <HelpIcon topicKey="partners.programs" />
              </legend>
              <div className="pa-program-options">
                {APPLY_PROGRAMS.map((program) => (
                  <label
                    key={program}
                    className={`pa-program ${form.program === program ? 'pa-program--selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="program"
                      value={program}
                      checked={form.program === program}
                      onChange={() => update('program', program)}
                      {...a11y('program')}
                    />
                    <span className="pa-program-name">
                      {t(`partnerApply.programs.${program}.name`)}
                    </span>
                    <span className="pa-program-desc">
                      {t(`partnerApply.programs.${program}.description`)}
                    </span>
                  </label>
                ))}
              </div>
              {renderFieldError('program')}
            </fieldset>

            <div className={fieldClass('name')}>
              <label htmlFor="pa-name">{t('partnerApply.fields.name')}</label>
              <input
                id="pa-name"
                name="name"
                type="text"
                autoComplete="name"
                value={form.name}
                maxLength={120}
                onChange={(e) => update('name', e.target.value)}
                {...a11y('name')}
              />
              {renderFieldError('name')}
            </div>

            <div className={fieldClass('email')}>
              <label htmlFor="pa-email">{t('partnerApply.fields.email')}</label>
              <input
                id="pa-email"
                name="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                {...a11y('email')}
              />
              {renderFieldError('email')}
            </div>

            <div className={fieldClass('phone')}>
              <label htmlFor="pa-phone">{t('partnerApply.fields.phone')}</label>
              <input
                id="pa-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={form.phone}
                maxLength={40}
                onChange={(e) => update('phone', e.target.value)}
                {...a11y('phone')}
              />
              {renderFieldError('phone')}
            </div>

            {(isInfluencer || isCm) && (
              <div className={fieldClass('instagram')}>
                <label htmlFor="pa-instagram">
                  {t(
                    isInfluencer
                      ? 'partnerApply.fields.instagram'
                      : 'partnerApply.fields.instagramOptional'
                  )}
                </label>
                <input
                  id="pa-instagram"
                  name="instagram"
                  type="text"
                  autoComplete="off"
                  placeholder="@usuario"
                  value={form.instagram}
                  maxLength={31}
                  onChange={(e) => update('instagram', e.target.value)}
                  {...a11y('instagram')}
                />
                {renderFieldError('instagram')}
              </div>
            )}

            {isCm && (
              <>
                <div className={fieldClass('locationsManaged')}>
                  <label htmlFor="pa-locations">{t('partnerApply.fields.locationsManaged')}</label>
                  <input
                    id="pa-locations"
                    name="locationsManaged"
                    type="number"
                    min="0"
                    max="10000"
                    step="1"
                    inputMode="numeric"
                    value={form.locationsManaged}
                    onChange={(e) => update('locationsManaged', e.target.value)}
                    {...a11y('locationsManaged')}
                  />
                  {renderFieldError('locationsManaged')}
                </div>
                <div className="form-group-checkbox">
                  <label htmlFor="pa-resale">
                    <input
                      id="pa-resale"
                      name="resaleInterest"
                      type="checkbox"
                      checked={form.resaleInterest}
                      onChange={(e) => update('resaleInterest', e.target.checked)}
                    />
                    <span>
                      {t('partnerApply.fields.resaleInterest')}
                      <span className="pa-hint">{t('partnerApply.fields.resaleHint')}</span>
                    </span>
                  </label>
                </div>
              </>
            )}

            <div className={fieldClass('message')}>
              <label htmlFor="pa-message">{t('partnerApply.fields.message')}</label>
              <textarea
                id="pa-message"
                name="message"
                rows={4}
                maxLength={1000}
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                {...a11y('message')}
              />
              {renderFieldError('message')}
            </div>

            {/* Campo trampa: fuera de pantalla, sin label visible; un humano no lo completa */}
            <div className="pa-hp" aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => update('website', e.target.value)}
              />
            </div>

            {error && (
              <div className="error-message" role="alert" data-testid={`pa-error-${error}`}>
                {t(`partnerApply.errors.${error}`)}
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {submitting ? t('partnerApply.submitting') : t('partnerApply.submit')}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <p>
            {t('partnerApply.alreadyPartner')}{' '}
            <Link to="/login" data-testid="pa-login-link">
              {t('partnerApply.alreadyPartnerCta')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function PartnerApplyPage() {
  // Página pública sin AppLayout: HelpIcon necesita TourProvider.
  return (
    <TourProvider>
      <PartnerApplyContent />
    </TourProvider>
  );
}

export default PartnerApplyPage;
